/**
 * A corded scanner in COM-port mode, read by the browser itself.
 *
 * Web Serial rather than a helper program on every workstation: the alternative
 * is an executable that has to be installed, kept running, updated and trusted
 * on each PC, and the whole of it would be "read a line, hand it over". The
 * price is that this path exists only in Chromium and only over HTTPS, which is
 * why keyboard mode stays the default and this is offered as a choice.
 *
 * Permission is granted once, per port, by the person — `requestPort()` opens
 * the browser's own picker. After that `getPorts()` returns it on every later
 * visit, so the dialog is not part of the daily routine.
 */

export type SerialSupport = 'ok' | 'unsupported' | 'insecure';

/** Baud rates worth offering. 9600 is what scanners leave the factory at. */
export const BAUD_RATES = [9600, 19200, 38400, 57600, 115200] as const;

/**
 * A scanner that sends no terminator would otherwise never deliver anything, so
 * a quiet line this long is treated as the end of a reading. Long enough that no
 * code is ever cut in half between two USB frames.
 */
const IDLE_FLUSH_MS = 80;

export function serialSupport(): SerialSupport {
  if (!window.isSecureContext) return 'insecure';
  if (!navigator.serial) return 'unsupported';
  return 'ok';
}

/** Ask the person to choose a port. Rejects if they close the picker. */
export async function requestPort(): Promise<boolean> {
  if (!navigator.serial) return false;
  try {
    await navigator.serial.requestPort();
    return true;
  } catch {
    // The picker was dismissed. Not an error: nothing was promised.
    return false;
  }
}

export async function knownPorts(): Promise<SerialPort[]> {
  if (!navigator.serial) return [];
  return navigator.serial.getPorts();
}

/** Withdraw the permission, so the next connection asks again. */
export async function forgetPorts(): Promise<void> {
  for (const port of await knownPorts()) {
    await port.forget?.();
  }
}

export interface SerialSession {
  stop(): Promise<void>;
}

/** Whether a port has already been granted to this site. The answer decides
 *  whether "COM mode" does anything at all, and it is the first thing a person
 *  wondering why nothing happens needs to be told. */
export async function hasPort(): Promise<boolean> {
  return (await knownPorts()).length > 0;
}

export interface LineAssembler {
  /** Feed whatever arrived. May be half a code, or three codes at once. */
  push(text: string): void;
  /** Give up waiting for a terminator and deliver what is held. */
  flush(): void;
}

/**
 * Turn a byte stream into readings.
 *
 * Pulled out of `open` because this is the part that can be wrong in a way
 * nobody notices: a code split across two USB frames arrives as two chunks, and
 * a reader that treats each chunk as a reading produces two halves of a code
 * that both fail to resolve, intermittently, on some scanners only.
 */
export function assembleLines(onValue: (value: string) => void): LineAssembler {
  let pending = '';

  return {
    push(text: string) {
      pending += text;
      // Scanners end a reading with CR, LF or CR+LF, depending on the model and
      // how it was configured. All three, and the tail is kept for next time.
      const parts = pending.split(/\r\n|\r|\n/);
      pending = parts.pop() ?? '';
      for (const part of parts) {
        const value = part.trim();
        if (value) onValue(value);
      }
    },
    flush() {
      const value = pending.trim();
      pending = '';
      if (value) onValue(value);
    },
  };
}

/**
 * Open the first port already granted and read codes from it.
 *
 * Returns null when there is no such port — the caller shows "choose a port"
 * rather than an error, because not having chosen one yet is the normal state.
 */
export async function open(
  onReading: (value: string) => void,
  options: { baudRate: number },
): Promise<SerialSession | null> {
  const [port] = await knownPorts();
  if (!port) return null;

  await port.open({ baudRate: options.baudRate });

  let stopped = false;
  let idle: ReturnType<typeof setTimeout> | null = null;
  const decoder = new TextDecoder();
  const reader = port.readable?.getReader() ?? null;
  const lines = assembleLines(onReading);

  const consume = (text: string) => {
    if (idle) clearTimeout(idle);
    lines.push(text);
    // A scanner that sends no terminator would otherwise never deliver
    // anything; a quiet line is taken as the end of a reading.
    idle = setTimeout(() => lines.flush(), IDLE_FLUSH_MS);
  };

  const pump = async () => {
    if (!reader) return;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done || stopped) break;
        if (value) consume(decoder.decode(value, { stream: true }));
      }
    } catch {
      // The cable was pulled, or the port was closed under us. Either way there
      // is nothing to recover: the caller reconnects when the person asks.
    }
  };

  void pump();

  return {
    async stop() {
      stopped = true;
      if (idle) clearTimeout(idle);
      try {
        await reader?.cancel();
        reader?.releaseLock();
        await port.close();
      } catch {
        // Already gone.
      }
    },
  };
}
